<template>
  <div class="labourer-schedule-view">
    <div class="md-layout">
      <!-- Left Column - Takeon Summary Card -->
      <div class="md-layout-item md-small-size-100 md-size-30">
        <md-card class="takeon-summary-card">
          <md-card-header class="md-card-header-icon md-card-header-primary">
            <div class="card-icon">
              <md-icon>confirmation_number</md-icon>
            </div>
            <h4 class="title">Takeon Information</h4>
            <p class="category">Takeon #{{ labourerSchedule.takeon_number }}</p>
          </md-card-header>

          <div class="card-content">
            <div class="takeon-status" :class="getStatusClass(labourerSchedule.status)">
              <md-icon>{{ getStatusIcon(labourerSchedule.status) }}</md-icon>
              <span>{{ getStatusLabel(labourerSchedule.status) }}</span>
            </div>

            <md-table class="info-table">
              <row-item-view label="Takeon Number" :value="labourerSchedule.takeon_number || 'N/A'" icon="confirmation_number"/>
              <row-item-view label="Status" :value="getStatusLabel(labourerSchedule.status)" icon="info">
                <template slot="value">
                  <badge :type="getStatusBadge(labourerSchedule.status)" class="status-badge">
                    {{ getStatusLabel(labourerSchedule.status) }}
                  </badge>
                </template>
              </row-item-view>
              <row-item-view label="Labourer" :value="getLabourerName()" icon="person"/>
              <row-item-view label="Activity" :value="getActivityName()" icon="work"/>
              <row-item-view label="Workplace" :value="getWorkplaceName()" icon="business"/>
              <row-item-view label="Assignment Remark" :value="labourerSchedule.assignment_remark || 'N/A'" icon="comment"/>
            </md-table>
          </div>
        </md-card>
      </div>

      <!-- Right Column - Tabs -->
      <div class="md-layout-item md-small-size-100 md-size-70">
        <md-card class="details-card">
          <md-card-content>
            <nav-tabs-card>
              <template slot="content">
                <md-tabs class="md-info" md-alignment="left">

                  <!-- Tab 1: Check-in/out Timeline -->
                  <md-tab id="tab-checkinout" md-label="Check-in/out" md-icon="login">
                    <div class="timeline-container">
                      <div class="timeline-step" :class="{ completed: labourerSchedule.checkin_at }">
                        <div class="step-icon">
                          <md-icon>login</md-icon>
                        </div>
                        <div class="step-content">
                          <div class="step-title">Gate Check In</div>
                          <div class="step-details">
                            <span class="step-time">{{ formatDateTime(labourerSchedule.checkin_at) || 'Not checked in' }}</span>
                            <span class="step-method" v-if="labourerSchedule.check_in_type">
                              <badge type="info" size="small">{{ labourerSchedule.check_in_type }}</badge>
                            </span>
                          </div>
                          <div class="step-remark" v-if="labourerSchedule.assignment_remark">
                            Remark: {{ labourerSchedule.assignment_remark }}
                          </div>
                        </div>
                      </div>

                      <div class="timeline-step" :class="{ completed: labourerSchedule.supervisor_checkin_at }">
                        <div class="step-icon">
                          <md-icon>verified_user</md-icon>
                        </div>
                        <div class="step-content">
                          <div class="step-title">Supervisor Check In</div>
                          <div class="step-details">
                            <span class="step-time">{{ formatDateTime(labourerSchedule.supervisor_checkin_at) || 'Pending' }}</span>
                          </div>
                          <div class="step-remark" v-if="labourerSchedule.checkin_supervisor_comment">
                            Comment: {{ labourerSchedule.checkin_supervisor_comment }}
                          </div>
                          <div class="step-reaction" v-if="labourerSchedule.checkin_supervisor_reaction">
                            Reaction: {{ getReactionLabel(labourerSchedule.checkin_supervisor_reaction) }}
                          </div>
                        </div>
                      </div>

                      <div class="timeline-step" :class="{ completed: labourerSchedule.supervisor_checkout_at }">
                        <div class="step-icon">
                          <md-icon>exit_to_app</md-icon>
                        </div>
                        <div class="step-content">
                          <div class="step-title">Supervisor Check Out</div>
                          <div class="step-details">
                            <span class="step-time">{{ formatDateTime(labourerSchedule.supervisor_checkout_at) || 'Pending' }}</span>
                          </div>
                          <div class="step-remark" v-if="labourerSchedule.supervisor_checkout_comment">
                            Comment: {{ labourerSchedule.supervisor_checkout_comment }}
                          </div>
                          <div class="step-reaction" v-if="labourerSchedule.checkout_supervisor_reaction">
                            Reaction: {{ getReactionLabel(labourerSchedule.checkout_supervisor_reaction) }}
                          </div>
                        </div>
                      </div>

                      <div class="timeline-step" :class="{ completed: labourerSchedule.checkout_at }">
                        <div class="step-icon">
                          <md-icon>logout</md-icon>
                        </div>
                        <div class="step-content">
                          <div class="step-title">Gate Check Out</div>
                          <div class="step-details">
                            <span class="step-time">{{ formatDateTime(labourerSchedule.checkout_at) || 'Not checked out' }}</span>
                            <span class="step-method" v-if="labourerSchedule.check_out_type">
                              <badge type="info" size="small">{{ labourerSchedule.check_out_type }}</badge>
                            </span>
                          </div>
                          <div class="step-remark" v-if="labourerSchedule.checkout_comment">
                            Remark: {{ labourerSchedule.checkout_comment }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </md-tab>

                  <!-- Tab 2: Schedule Details -->
                  <md-tab id="tab-schedule" md-label="Schedule" md-icon="schedule">
                    <div class="details-grid">
                      <div class="detail-card">
                        <div class="detail-icon">
                          <md-icon>event</md-icon>
                        </div>
                        <div class="detail-content">
                          <div class="detail-label">Shift Date</div>
                          <div class="detail-value">{{ toLocal(getScheduleValue('shift_date')) || 'N/A' }}</div>
                        </div>
                      </div>

                      <div class="detail-card">
                        <div class="detail-icon">
                          <md-icon>access_time</md-icon>
                        </div>
                        <div class="detail-content">
                          <div class="detail-label">Start Time</div>
                          <div class="detail-value">{{ formatTime(getScheduleValue('start_time')) || 'N/A' }}</div>
                        </div>
                      </div>

                      <div class="detail-card">
                        <div class="detail-icon">
                          <md-icon>access_time</md-icon>
                        </div>
                        <div class="detail-content">
                          <div class="detail-label">End Time</div>
                          <div class="detail-value">{{ formatTime(getScheduleValue('end_time')) || 'N/A' }}</div>
                        </div>
                      </div>

                      <div class="detail-card">
                        <div class="detail-icon">
                          <md-icon>people</md-icon>
                        </div>
                        <div class="detail-content">
                          <div class="detail-label">Total Labourers Required</div>
                          <div class="detail-value">{{ getScheduleValue('labourer_count') || 0 }}</div>
                        </div>
                      </div>

                      <div class="detail-card full-width">
                        <div class="detail-icon">
                          <md-icon>comment</md-icon>
                        </div>
                        <div class="detail-content">
                          <div class="detail-label">Schedule Remark</div>
                          <div class="detail-value">{{ getScheduleValue('remark') || 'No remarks' }}</div>
                        </div>
                      </div>
                    </div>
                  </md-tab>

                  <!-- Tab 3: Labourer Details -->
                  <md-tab id="tab-labourer" md-label="Labourer" md-icon="person">
                    <div class="labourer-profile">
                      <div class="labourer-avatar">
                        <img v-if="labourerSchedule.labourer && labourerSchedule.labourer.image" :src="getImageUrl(labourerSchedule.labourer.image)" />
                        <md-icon v-else class="avatar-placeholder">person</md-icon>
                      </div>
                      <div class="labourer-info">
                        <h3>{{ getLabourerName() }}</h3>
                        <div class="labourer-meta">
                          <span class="meta-item">
                            <md-icon>badge</md-icon>
                            {{ getLabourerValue('labourer_number') }}
                          </span>
                          <span class="meta-item">
                            <md-icon>credit_card</md-icon>
                            {{ getLabourerValue('nida_number') || 'N/A' }}
                          </span>
                          <span class="meta-item">
                            <md-icon>phone</md-icon>
                            {{ getLabourerValue('phone_number') || 'N/A' }}
                          </span>
                          <span class="meta-item">
                            <md-icon>wc</md-icon>
                            {{ getLabourerValue('gender') || 'N/A' }}
                          </span>
                        </div>
                        <div class="labourer-skills" v-if="getLabourerSkills().length">
                          <div class="skills-title">Skills:</div>
                          <div class="skills-list">
                            <badge v-for="skill in getLabourerSkills()" :key="skill.id" type="success" size="small">
                              {{ skill.name }}
                            </badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </md-tab>

                  <!-- Tab 4: Status Timeline -->
                  <md-tab id="tab-status" md-label="Status History" md-icon="timeline">
                    <div class="status-timeline">
                      <div v-for="(status, index) in labourerSchedule.statuses" :key="index" class="status-item">
                        <div class="status-marker" :class="getStatusMarkerClass(status.name)">
                          <md-icon>{{ getStatusMarkerIcon(status.name) }}</md-icon>
                        </div>
                        <div class="status-content">
                          <div class="status-name">{{ status.name }}</div>
                          <div class="status-reason" v-if="status.reason">{{ status.reason }}</div>
                          <div class="status-date">{{ formatDateTime(status.created_at) }}</div>
                          <div class="status-creator" v-if="status.creator">
                            By: {{ status.creator.name }}
                          </div>
                        </div>
                      </div>
                      <div v-if="!labourerSchedule.statuses || !labourerSchedule.statuses.length" class="empty-status">
                        <md-icon>timeline</md-icon>
                        <p>No status history available</p>
                      </div>
                    </div>
                  </md-tab>

                  <!-- Tab 5: Activity & Workplace -->
                  <md-tab id="tab-activity" md-label="Activity & Workplace" md-icon="business_center">
                    <div class="activity-workplace-info">
                      <div class="info-section">
                        <h5><md-icon>work</md-icon> Activity Information</h5>
                        <div class="info-grid">
                          <div class="info-row">
                            <span class="info-label">Activity Name:</span>
                            <span class="info-value">{{ getActivityName() }}</span>
                          </div>
                          <div class="info-row">
                            <span class="info-label">Payment Mode:</span>
                            <span class="info-value">{{ getActivityValue('payment_mode') || 'N/A' }}</span>
                          </div>
                          <div class="info-row">
                            <span class="info-label">Pay Code:</span>
                            <span class="info-value">{{ getActivityValue('pay_code') || 'N/A' }}</span>
                          </div>
                          <div class="info-row">
                            <span class="info-label">Description:</span>
                            <span class="info-value">{{ getActivityValue('description') || 'N/A' }}</span>
                          </div>
                        </div>
                      </div>

                      <div class="info-section">
                        <h5><md-icon>business</md-icon> Workplace Information</h5>
                        <div class="info-grid">
                          <div class="info-row">
                            <span class="info-label">Workplace Name:</span>
                            <span class="info-value">{{ getWorkplaceName() }}</span>
                          </div>
                          <div class="info-row">
                            <span class="info-label">Location:</span>
                            <span class="info-value">{{ getWorkplaceValue('location') || 'N/A' }}</span>
                          </div>
                          <div class="info-row">
                            <span class="info-label">Status:</span>
                            <span class="info-value">
                              <badge :type="getWorkplaceStatusBadge(getWorkplaceValue('status'))" size="small">
                                {{ getWorkplaceValue('status') || 'Active' }}
                              </badge>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </md-tab>
                </md-tabs>
              </template>
            </nav-tabs-card>
          </md-card-content>
        </md-card>
      </div>
    </div>
  </div>
</template>

<script>
import DateFormatMixin from '@/mixins/date-format-mixin';
import { NavTabsCard } from '@/components';
import RowItemView from '../../../components/Inputs/RowItemView';
import Badge from '@/components/Badge.vue';
import formMixin from '@/mixins/form-mixin';

export default {
  name: 'ViewLabourerSchedulePage',
  components: {
    RowItemView,
    NavTabsCard,
    Badge
  },
  mixins: [formMixin,DateFormatMixin],
  data: function() {
    return {
      labourerSchedule: {
        schedule: {},
        activity: {},
        workplace: {},
        labourer: {},
        statuses: []
      },
      isLoading: false
    };
  },

  created: function() {
    this.get();
  },

  methods: {
    async get() {
      this.isLoading = true;
      try {
        var id = this.$route.params.id;
        var params = {
          include: 'labourer,activity,workplace,schedule,statuses,statuses.creator',
          id: id
        };
        await this.$store.dispatch('labourerSchedules/view', params);
        this.labourerSchedule = this.$store.getters['labourerSchedules/labourerSchedule'];
      } catch (e) {
        console.error(e);
        this.notifyError('Failed to load takeon details');
      } finally {
        this.isLoading = false;
      }
    },

    getLabourerName: function() {
      if (!this.labourerSchedule.labourer) return 'N/A';
      var labourer = this.labourerSchedule.labourer;
      var firstName = labourer.first_name || '';
      var middleName = labourer.middle_name || '';
      var lastName = labourer.last_name || '';
      var parts = [];
      if (firstName) parts.push(firstName);
      if (middleName) parts.push(middleName);
      if (lastName) parts.push(lastName);
      return parts.length ? parts.join(' ') : 'N/A';
    },

    getLabourerValue: function(field) {
      if (this.labourerSchedule.labourer) {
        return this.labourerSchedule.labourer[field];
      }
      return null;
    },

    getLabourerSkills: function() {
      if (this.labourerSchedule.labourer && this.labourerSchedule.labourer.skills) {
        return this.labourerSchedule.labourer.skills;
      }
      return [];
    },

    getActivityName: function() {
      if (this.labourerSchedule.activity && this.labourerSchedule.activity.name) {
        return this.labourerSchedule.activity.name;
      }
      return 'N/A';
    },

    getActivityValue: function(field) {
      if (this.labourerSchedule.activity) {
        return this.labourerSchedule.activity[field];
      }
      return null;
    },

    getWorkplaceName: function() {
      if (this.labourerSchedule.workplace && this.labourerSchedule.workplace.name) {
        return this.labourerSchedule.workplace.name;
      }
      return 'N/A';
    },

    getWorkplaceValue: function(field) {
      if (this.labourerSchedule.workplace) {
        return this.labourerSchedule.workplace[field];
      }
      return null;
    },

    getScheduleValue: function(field) {
      console.log('getScheduleValue '+ field);
      console.log('getScheduleValue',this.labourerSchedule.schedule);
      if (this.labourerSchedule.schedule) {
        return this.labourerSchedule.schedule[field];
      }
      return null;
    },

    getStatusLabel: function(status) {
      var statusMap = {
        'pending': 'Pending',
        'checked_in': 'Checked In',
        'confirmed': 'Confirmed',
        'checked_out': 'Checked Out',
        'completed': 'Completed',
        'Check In': 'Check In',
        'Checkin Supervisor': 'Checkin Supervisor',
        'Checkout Supervisor': 'Checkout Supervisor',
        'Check Out': 'Check Out'
      };
      return statusMap[status] || status || 'Unknown';
    },

    getStatusBadge: function(status) {
      var badgeMap = {
        'pending': 'warning',
        'checked_in': 'info',
        'confirmed': 'success',
        'checked_out': 'warning',
        'completed': 'success',
        'Check In': 'info',
        'Checkin Supervisor': 'success',
        'Checkout Supervisor': 'warning',
        'Check Out': 'success'
      };
      return badgeMap[status] || 'default';
    },

    getStatusIcon: function(status) {
      var iconMap = {
        'pending': 'hourglass_empty',
        'checked_in': 'login',
        'confirmed': 'verified',
        'checked_out': 'logout',
        'completed': 'check_circle',
        'Check In': 'login',
        'Checkin Supervisor': 'verified_user',
        'Checkout Supervisor': 'exit_to_app',
        'Check Out': 'logout'
      };
      return iconMap[status] || 'info';
    },

    getStatusClass: function(status) {
      var classMap = {
        'pending': 'status-pending',
        'checked_in': 'status-checkedin',
        'confirmed': 'status-confirmed',
        'checked_out': 'status-checkedout',
        'completed': 'status-completed'
      };
      return classMap[status] || '';
    },

    getReactionLabel: function(reaction) {
      var reactionMap = {
        'on_time': 'On Time',
        'late': 'Late',
        'completed': 'Completed',
        'partial': 'Partial Completion',
        'early_departure': 'Early Departure',
        'present': 'Present',
        'absent': 'Absent'
      };
      return reactionMap[reaction] || reaction || 'N/A';
    },

    getStatusMarkerClass: function(status) {
      var classMap = {
        'Check In': 'marker-info',
        'Checkin Supervisor': 'marker-success',
        'Checkout Supervisor': 'marker-warning',
        'Check Out': 'marker-success',
        'completed': 'marker-success'
      };
      return classMap[status] || 'marker-default';
    },

    getStatusMarkerIcon: function(status) {
      var iconMap = {
        'Check In': 'login',
        'Checkin Supervisor': 'verified_user',
        'Checkout Supervisor': 'exit_to_app',
        'Check Out': 'logout',
        'completed': 'check_circle'
      };
      return iconMap[status] || 'radio_button_checked';
    },

    getWorkplaceStatusBadge: function(status) {
      var badgeMap = {
        'Active': 'success',
        'Inactive': 'danger',
        'Maintenance': 'warning'
      };
      return badgeMap[status] || 'info';
    },

    formatDateTime: function(date) {
      if (!date) return null;
      try {
        return new Date(date).toLocaleString();
      } catch (e) {
        return date;
      }
    },

    formatDate: function(date) {
      if (!date) return null;
      try {
        return new Date(date).toLocaleDateString();
      } catch (e) {
        return date;
      }
    },

    formatTime: function(time) {
      if (!time) return null;
      try {
        return new Date(time).toLocaleTimeString();
      } catch (e) {
        return time;
      }
    },

    getImageUrl: function(image) {
      if (!image) return null;
      if (image.startsWith('http')) return image;
      return process.env.VUE_APP_BASE_URL + image;
    }
  }
};
</script>

<style scoped>
.labourer-schedule-view {
  padding: 20px;
}

/* Takeon Summary Card */
.takeon-summary-card {
  margin-bottom: 20px;
}

.takeon-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  margin-bottom: 20px;
  font-weight: 500;
}

.status-pending {
  background: #fff3e0;
  color: #ff9800;
}

.status-checkedin {
  background: #e3f2fd;
  color: #2196f3;
}

.status-confirmed {
  background: #e8f5e9;
  color: #4caf50;
}

.status-checkedout {
  background: #fff3e0;
  color: #ff9800;
}

.status-completed {
  background: #e8f5e9;
  color: #4caf50;
}

.status-badge {
  display: inline-block;
}

/* Info Table */
.info-table {
  width: 100%;
}

/* Timeline Container */
.timeline-container {
  padding: 20px;
}

.timeline-step {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  position: relative;
  opacity: 0.5;
  transition: opacity 0.3s ease;
}

.timeline-step.completed {
  opacity: 1;
}

.timeline-step:not(:last-child):before {
  content: '';
  position: absolute;
  left: 20px;
  top: 40px;
  bottom: -24px;
  width: 2px;
  background: #e0e0e0;
}

.timeline-step.completed:not(:last-child):before {
  background: #4caf50;
}

.step-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.timeline-step.completed .step-icon {
  background: #4caf50;
  color: white;
}

.step-content {
  flex: 1;
}

.step-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.step-details {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 4px;
  font-size: 13px;
  color: #666;
}

.step-time {
  font-size: 12px;
}

.step-remark,
.step-reaction {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

/* Details Grid */
.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
  padding: 20px;
}

.detail-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 8px;
  transition: transform 0.2s;
}

.detail-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.detail-card.full-width {
  grid-column: 1 / -1;
}

.detail-icon md-icon {
  font-size: 32px;
  color: #9c27b0;
}

.detail-content {
  flex: 1;
}

.detail-label {
  font-size: 11px;
  color: #999;
  margin-bottom: 4px;
}

.detail-value {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

/* Labourer Profile */
.labourer-profile {
  display: flex;
  gap: 24px;
  padding: 20px;
  flex-wrap: wrap;
}

.labourer-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.labourer-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  font-size: 60px;
  color: #999;
}

.labourer-info {
  flex: 1;
}

.labourer-info h3 {
  margin: 0 0 12px 0;
  font-size: 20px;
}

.labourer-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #666;
}

.meta-item md-icon {
  font-size: 18px;
}

.labourer-skills {
  margin-top: 12px;
}

.skills-title {
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 8px;
}

.skills-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* Status Timeline */
.status-timeline {
  padding: 20px;
}

.status-item {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  position: relative;
}

.status-item:not(:last-child):before {
  content: '';
  position: absolute;
  left: 16px;
  top: 32px;
  bottom: -20px;
  width: 2px;
  background: #e0e0e0;
}

.status-marker {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.marker-info {
  background: #e3f2fd;
  color: #2196f3;
}

.marker-success {
  background: #e8f5e9;
  color: #4caf50;
}

.marker-warning {
  background: #fff3e0;
  color: #ff9800;
}

.marker-default {
  background: #f5f5f5;
  color: #999;
}

.status-content {
  flex: 1;
}

.status-name {
  font-weight: 600;
  margin-bottom: 4px;
}

.status-reason {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.status-date {
  font-size: 11px;
  color: #999;
}

.status-creator {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}

.empty-status {
  text-align: center;
  padding: 40px;
  color: #999;
}

.empty-status md-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

/* Activity & Workplace Info */
.activity-workplace-info {
  padding: 20px;
}

.info-section {
  margin-bottom: 24px;
}

.info-section h5 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px 0;
  color: #9c27b0;
}

.info-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  flex-wrap: wrap;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-label {
  width: 140px;
  font-weight: 500;
  color: #666;
}

.info-value {
  flex: 1;
  color: #333;
}

/* Responsive */
@media (max-width: 768px) {
  .labourer-schedule-view {
    padding: 10px;
  }

  .labourer-profile {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .labourer-meta {
    justify-content: center;
  }

  .skills-list {
    justify-content: center;
  }

  .info-label {
    width: 100%;
    margin-bottom: 4px;
  }

  .info-row {
    flex-direction: column;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .timeline-step {
    flex-direction: column;
    align-items: flex-start;
  }

  .timeline-step:not(:last-child):before {
    left: 16px;
    top: 40px;
  }
}
</style>